import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { track } from "../lib/track";
import { useAuth } from "../auth/AuthContext";
import ExplainPanel from "../components/ExplainPanel";
import MemoryInfluence from "../components/MemoryInfluence";
import "./ParishIntelligence.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GROUPS = [
  { n: "01", title: "General Information", fields: [
    ["parish_name", "Parish / Church Name", "input", true],
    ["denomination", "Denomination", "input"], ["diocese", "Diocese", "input"],
    ["city", "City", "input"], ["state", "State", "input"],
    ["country", "Country", "input"], ["year_founded", "Year Founded", "input"],
    ["website", "Website", "input"],
  ]},
  { n: "02", title: "Leadership", fields: [
    ["rector_pastor", "Rector / Pastor", "input"], ["associate_clergy", "Associate Clergy", "input"],
    ["music_director", "Music Director", "input"], ["organist", "Organist", "input"],
    ["choir_director", "Choir Director", "input"],
  ]},
  { n: "03", title: "Congregation", fields: [
    ["average_attendance", "Average Sunday Attendance", "input"], ["membership", "Membership", "input"],
    ["num_services", "Number of Services", "input"], ["age_distribution", "Age Distribution", "input"],
  ]},
  { n: "04", title: "Worship & Music", fields: [
    ["worship_style", "Worship Style", "input"], ["liturgical_tradition", "Liturgical Tradition", "input"],
    ["sacramental_practice", "Sacramental Practice", "input"], ["music_style", "Music Style", "input"],
    ["choir_program", "Choir Program", "input"], ["organ", "Organ", "input"],
    ["piano", "Piano", "input"], ["praise_band", "Praise Band", "input"], ["bells", "Bells", "input"],
  ]},
  { n: "05", title: "Facilities", fields: [
    ["sanctuary", "Sanctuary", "input"], ["parish_hall", "Parish Hall", "input"],
    ["school", "School", "input"], ["community_spaces", "Community Spaces", "input"],
  ]},
  { n: "06", title: "Mission (SWOT)", fields: [
    ["strengths", "Strengths", "textarea"], ["challenges", "Challenges / Weaknesses", "textarea"],
    ["opportunities", "Opportunities", "textarea"], ["threats", "Threats", "textarea"],
  ]},
  { n: "07", title: "Community", fields: [
    ["neighborhood", "Neighborhood", "input"], ["demographics", "Demographics", "input"],
    ["outreach_ministries", "Outreach Ministries", "textarea"],
  ]},
  { n: "08", title: "Financial & People", fields: [
    ["budget", "Approximate Budget", "input"], ["staffing", "Staffing", "input"], ["volunteers", "Volunteers", "input"],
  ]},
  { n: "09", title: "Goals & Notes", fields: [
    ["short_term_goals", "Short-Term Goals", "textarea"], ["long_term_goals", "Long-Term Goals", "textarea"],
    ["additional_notes", "Additional Notes", "textarea"],
  ]},
];

const SECTIONS = [
  ["executive_summary", "Executive Summary", "p"],
  ["parish_identity_profile", "Parish Identity Profile", "p"],
  ["mission_analysis", "Mission Analysis", "p"],
  ["liturgical_identity", "Liturgical Identity", "p"],
  ["worship_intelligence", "Worship Intelligence™", "p"],
  ["music_ministry_intelligence", "Music Ministry Intelligence™", "p"],
  ["leadership_analysis", "Leadership Analysis", "p"],
  ["congregational_analysis", "Congregational Analysis", "p"],
  ["community_analysis", "Community Analysis", "p"],
  ["operational_analysis", "Operational Analysis", "p"],
  ["financial_risk_observations", "Financial Risk Observations", "p"],
  ["volunteer_capacity", "Volunteer Capacity", "p"],
  ["swot", "SWOT Analysis", "swot"],
  ["growth_opportunities", "Growth Opportunities", "ul"],
  ["risk_factors", "Risk Factors", "ul"],
  ["quick_wins", "Quick Wins", "ul"],
  ["ninety_day_action_plan", "90-Day Action Plan", "plan"],
  ["one_year_roadmap", "One-Year Strategic Roadmap", "roadmap"],
  ["five_year_vision", "Five-Year Vision", "ul"],
  ["recommendations", "Recommendations", "ul"],
  ["final_executive_summary", "Final Executive Summary", "p"],
];

function Gauge({ value }) {
  const r = 66, c = 2 * Math.PI * r, off = c - (Math.max(0, Math.min(100, value || 0)) / 100) * c;
  return (
    <div className="ppi-gauge">
      <svg width="150" height="150">
        <circle cx="75" cy="75" r={r} fill="none" stroke="#e6ddca" strokeWidth="10" />
        <circle cx="75" cy="75" r={r} fill="none" stroke="#B08A4A" strokeWidth="10"
          strokeDasharray={c} strokeDashoffset={off} strokeLinecap="round" />
      </svg>
      <div className="gv"><div className="num">{value ?? "—"}</div><div className="lbl">PI Score</div></div>
    </div>
  );
}

export default function ParishIntelligence() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const subscribed = ["parish_pro", "cathedral", "diocese"].includes(user?.entitlements?.plan) && user?.entitlements?.plan_status === "active";
  const [tab, setTab] = useState("new");
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [intake, setIntake] = useState(null);
  const [explain, setExplain] = useState(null);
  const [memoryPreview, setMemoryPreview] = useState(null);
  const [saved, setSaved] = useState(false);
  const [history, setHistory] = useState([]);
  const [search, setSearch] = useState("");
  const [upgrade, setUpgrade] = useState(null);

  const loadHistory = useCallback(async () => {
    try { const r = await axios.get(`${API}/parish/reports`); setHistory(r.data); }
    catch (e) { console.warn("Report history load failed:", e); }
  }, []);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const generate = async (e) => {
    e.preventDefault();
    if (!form.parish_name) { alert("Please enter the Parish / Church Name."); return; }
    setLoading(true); setSaved(false); setReport(null); setUpgrade(null); setExplain(null); setMemoryPreview(null); setTab("report");
    try {
      const start = await axios.post(`${API}/parish/generate`, form);
      const jobId = start.data.job_id;
      let attempts = 0;
      const poll = async () => {
        attempts += 1;
        try {
          const r = await axios.get(`${API}/parish/job/${jobId}`);
          if (r.data.status === "done") {
            setReport(r.data.report); setIntake(r.data.intake);
            setExplain(r.data.explain || null); setMemoryPreview(r.data.memory_preview || null);
            setLoading(false);
          } else if (r.data.status === "error") {
            alert(r.data.detail || "The report could not be generated."); setLoading(false); setTab("new");
          } else if (attempts > 60) {
            alert("This is taking longer than expected. Please try again."); setLoading(false); setTab("new");
          } else {
            setTimeout(poll, 3000);
          }
        } catch (err) {
          if (attempts > 60) { setLoading(false); setTab("new"); }
          else setTimeout(poll, 3000);
        }
      };
      setTimeout(poll, 3000);
    } catch (err) {
      setLoading(false); setTab("new");
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 402 && detail && typeof detail === "object") {
        setUpgrade(detail.message || "You've used your included report.");
      } else {
        alert((typeof detail === "string" ? detail : "") || "The report could not be started. Please try again.");
      }
    }
  };

  const save = async () => {
    if (!report) return;
    try { await axios.post(`${API}/parish/save`, { intake, report }); setSaved(true); loadHistory(); }
    catch (e) { alert("Could not save the report."); }
  };

  const openReport = async (id) => {
    setLoading(true); setTab("report");
    try { const r = await axios.get(`${API}/parish/reports/${id}`); setReport(r.data.report); setIntake(r.data.intake); setExplain(null); setMemoryPreview(null); setSaved(true); }
    catch (e) { alert("Could not open report."); }
    finally { setLoading(false); }
  };

  const copyReport = async () => {
    if (!report) return;
    const lines = [];
    SECTIONS.forEach(([k, title, t]) => {
      const v = report[k]; if (!v) return;
      lines.push(`\n## ${title}\n`);
      if (t === "p") lines.push(v);
      else if (t === "ul") lines.push((v || []).map((x) => `• ${x}`).join("\n"));
      else if (t === "swot") ["strengths","weaknesses","opportunities","threats"].forEach((q) => lines.push(`${q.toUpperCase()}: ` + (v[q]||[]).join("; ")));
      else if (t === "plan") lines.push((v||[]).map((x) => `• ${x.title}: ${x.detail}`).join("\n"));
      else if (t === "roadmap") lines.push((v||[]).map((x) => `• ${x.period}: ${x.focus}`).join("\n"));
    });
    await navigator.clipboard.writeText(`PARISH INTELLIGENCE REPORT — ${intake?.parish_name || ""}\n` + lines.join("\n"));
    alert("Report copied to clipboard.");
  };

  const sc = report?.scorecard || {};
  const filtered = history.filter((h) => (h.parish_name || "").toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="ppi">
      <div className="ppi-top">
        <div className="ppi-top-in">
          <div className="ppi-brand">
            <div className="b1">Parish Profile <span>Intelligence™</span></div>
            <div className="b2">Liturgical Intelligence™ · Powered by Ordo AI™</div>
          </div>
          <div className="ppi-tabs">
            <button className={`ppi-tab${tab==="new"?" active":""}`} onClick={() => setTab("new")} data-testid="tab-new">New Assessment</button>
            <button className={`ppi-tab${tab==="report"?" active":""}`} onClick={() => setTab("report")} data-testid="tab-report" disabled={!report && !loading}>Report</button>
            <button className={`ppi-tab${tab==="history"?" active":""}`} onClick={() => { setTab("history"); loadHistory(); }} data-testid="tab-history">History</button>
          </div>
        </div>
      </div>

      <div className="ppi-wrap">
        {upgrade && (
          <div className="ppi-upsell-overlay" data-testid="upgrade-modal">
            <div className="ppi-upsell">
              <div className="ppi-upsell-eyebrow">Report limit reached</div>
              <h3>Unlock more Parish Intelligence™</h3>
              <p>{upgrade}</p>
              <div className="ppi-upsell-actions">
                <button className="ppi-btn gold" onClick={() => navigate("/account")} data-testid="upsell-parish-pro">Upgrade to Parish Pro</button>
                <button className="ppi-btn" onClick={() => navigate("/account")} data-testid="upsell-single">Purchase Another Single Report ($29)</button>
                <button className="ppi-btn ghost" onClick={() => { setUpgrade(null); setTab("new"); }} data-testid="upsell-dismiss">Maybe Later</button>
              </div>
            </div>
          </div>
        )}

        {tab === "new" && (
          <form onSubmit={generate} data-testid="assessment-form">
            <div className="ppi-eyebrow">New Parish Assessment</div>
            <h1 className="ppi-h1">Build a strategic intelligence profile of your parish.</h1>
            <p className="ppi-lead">Complete the assessment below. Ordo AI™ analyzes your parish across leadership, worship, music, community, and growth — then produces an executive-grade Parish Intelligence Report.</p>
            {GROUPS.map((g) => (
              <div className="ppi-group" key={g.n}>
                <div className="ppi-group-h"><span className="n">{g.n}</span> {g.title}</div>
                <div className="ppi-fields">
                  {g.fields.map(([key, label, type, req]) => (
                    <div className={`ppi-field${type==="textarea"?" full":""}`} key={key}>
                      <label>{label}{req ? " *" : ""}</label>
                      {type === "textarea"
                        ? <textarea value={form[key]||""} onChange={set(key)} data-testid={`f-${key}`} />
                        : <input value={form[key]||""} onChange={set(key)} required={!!req} data-testid={`f-${key}`} />}
                    </div>
                  ))}
                </div>
              </div>
            ))}
            <div className="ppi-actions">
              <button className="ppi-btn gold" type="submit" disabled={loading} data-testid="generate-btn">
                {loading ? "Generating…" : "Generate Parish Intelligence Report"}
              </button>
            </div>
          </form>
        )}

        {tab === "report" && (
          loading ? (
            <div className="ppi-loading" data-testid="report-loading">
              <div className="spin" />
              <h3>Ordo AI™ is analyzing your parish…</h3>
              <p>Synthesizing leadership, worship, music, community & growth. This takes up to ~90 seconds.</p>
            </div>
          ) : report ? (
            <>
              <div className="ppi-report" data-testid="report">
                <div className="ppi-rep-head">
                  <div className="tag">Parish Intelligence Report · Ordo AI™</div>
                  <h2>{intake?.parish_name || "Parish"}</h2>
                  <div className="sub">{[intake?.city, intake?.state, intake?.denomination].filter(Boolean).join(" · ")}</div>
                </div>
                <MemoryInfluence explain={explain} subscribed={subscribed} />
                <div className="ppi-overall">
                  <Gauge value={sc.overall_score} />
                  <div className="ppi-overall-meta">
                    <h3>Overall Parish Intelligence Score</h3>
                    {sc.overall_grade && <span className="ppi-grade">Grade {sc.overall_grade}</span>}
                  </div>
                </div>
                <div className="ppi-scores">
                  {(sc.categories || []).map((c) => (
                    <div className="ppi-score" key={c.name} data-testid={`score-${c.name}`}>
                      <div className="sn">{c.name}</div>
                      <div className="sv"><b>{c.score}</b><span>/100</span></div>
                      <div className="ppi-bar"><i style={{ width: `${c.score}%` }} /></div>
                    </div>
                  ))}
                </div>
                <div className="ppi-sections">
                  {SECTIONS.map(([k, title, t]) => {
                    const v = report[k];
                    if (!v || (Array.isArray(v) && !v.length)) return null;
                    return (
                      <div className="ppi-sec" key={k}>
                        <h3>{title}</h3>
                        {t === "p" && <p>{v}</p>}
                        {t === "ul" && <ul>{v.map((x, i) => <li key={`${x}-${i}`}>{x}</li>)}</ul>}
                        {t === "swot" && (
                          <div className="ppi-swot">
                            {["strengths","weaknesses","opportunities","threats"].map((q) => (
                              <div className="q" key={q}><h4>{q}</h4><ul>{(v[q]||[]).map((x,i)=><li key={`${x}-${i}`}>{x}</li>)}</ul></div>
                            ))}
                          </div>
                        )}
                        {t === "plan" && <div className="ppi-plan">{v.map((x,i)=><div className="item" key={`${x.title}-${i}`}><b>{x.title}</b><span>{x.detail}</span></div>)}</div>}
                        {t === "roadmap" && <div className="ppi-plan">{v.map((x,i)=><div className="item" key={`${x.period}-${i}`}><b>{x.period}</b><span>{x.focus}</span></div>)}</div>}
                      </div>
                    );
                  })}
                </div>
              </div>
              <ExplainPanel explain={explain} subscribed={subscribed} memoryPreview={memoryPreview} />
              <div className="ppi-actions">
                <button className="ppi-btn gold" onClick={save} data-testid="save-btn">{saved ? "Saved ✓" : "Save Report"}</button>
                <button className="ppi-btn" onClick={() => { track("PDF Export", { product: "parish_intelligence" }); window.print(); }} data-testid="print-btn">Export / Print PDF</button>
                <button className="ppi-btn ghost" onClick={copyReport} data-testid="copy-btn">Copy Report</button>
                <button className="ppi-btn ghost" onClick={() => setTab("new")} data-testid="another-btn">New Assessment</button>
              </div>
            </>
          ) : (
            <div className="ppi-empty">No report yet. Start a new assessment.</div>
          )
        )}

        {tab === "history" && (
          <>
            <div className="ppi-eyebrow">Saved Reports</div>
            <h1 className="ppi-h1">Report History</h1>
            <input className="ppi-search" placeholder="Search by parish name…" value={search} onChange={(e)=>setSearch(e.target.value)} data-testid="history-search" />
            {filtered.length ? (
              <div className="ppi-hist">
                {filtered.map((h) => (
                  <div className="ppi-card" key={h.id} onClick={() => openReport(h.id)} data-testid={`hist-${h.id}`}>
                    {h.overall_score != null && <div className="scorepill">{h.overall_score}</div>}
                    <h4>{h.parish_name}</h4>
                    <div className="meta">{h.city_state || ""} · {new Date(h.created_at).toLocaleDateString()}</div>
                  </div>
                ))}
              </div>
            ) : <div className="ppi-empty">No saved reports yet.</div>}
          </>
        )}
      </div>
    </div>
  );
}
